/**
 * Why a session failed, in words (ADR 0059 decision 1, R-HL-3): one `SessionFailure` per failed
 * session, classified from its last message, stop reason, exit code and API error signals. Both
 * agents send it as `Session.ext.failure` (the Go agent mirrors classifyFailure with a parity
 * test); the drawer opens on its cause and the one fix the cause offers. Pure and browser-safe.
 *
 * The model check (ADR 0059 decision 4) lives here too: a launch with a model id that does not
 * exist is refused before Claude Code starts, with the models that do.
 */
import type { Session, SessionStatus, Ts } from '../index.ts';
import { clip } from '../index.ts';
import { SESSION_ERROR_RE } from './missions.ts';
import { KNOWN_CLAUDE_MODELS, normalizeModelId } from './session-usage.ts';
import { isRegexSpace, trimEndOf } from './trim.ts';
import type { RuntimeFault } from './runtime-faults.ts';
import { runtimeFaultSentence } from './runtime-faults.ts';

/** The key under Session.ext. */
export const SESSION_FAILURE_EXT = 'failure';

/**
 * 'environment' (20 Oct 2026): the run ended cleanly but says its sandbox or file permissions
 * stopped the work (agent-health.ts environmentFailureOf). classifyFailure never returns it, so
 * the Go agent's parity fixtures are unchanged; the hub reads it.
 *
 * 'runtime' (ADR 0089, 20 Oct 2026): the agent's own environment or tooling is broken (a shell,
 * git or a package manager that cannot work), read from what its commands printed
 * (runtime-faults.ts). classifyFailure never returns it either.
 */
export type FailureCause = 'model-unavailable' | 'auth' | 'usage-limit' | 'api-error' | 'blocked' | 'crash' | 'launch' | 'task' | 'environment' | 'runtime';
export const FAILURE_CAUSES: readonly FailureCause[] = ['model-unavailable', 'auth', 'usage-limit', 'api-error', 'blocked', 'crash', 'launch', 'task', 'environment', 'runtime'];

/** How each cause reads as a label: "Failed: model unavailable". */
export const FAILURE_CAUSE_LABEL: Record<FailureCause, string> = {
  'model-unavailable': 'Model unavailable',
  auth: 'Signed out',
  'usage-limit': 'Usage limit',
  'api-error': 'API error',
  blocked: 'Blocked',
  crash: 'Crashed',
  launch: 'Could not start',
  task: 'Task failed',
  environment: 'Environment blocked',
  runtime: 'Runtime broken',
};

export interface SessionFailure {
  cause: FailureCause;
  /** One plain sentence: what failed and why. Never an em dash. */
  summary: string;
  /** The raw message it was read from, clipped (FAILURE_DETAIL_MAX). */
  detail?: string;
  /** The model it failed on (model-unavailable), as the error named it. */
  model?: string;
  /** When a usage limit resets, when the error said (ms). */
  resetsAt?: Ts;
  /** The process exit code (crash). */
  exitCode?: number;
  /**
   * Its process ended with the Orgus agent that ran it (a hub or agent restart), not on its own
   * (ADR 0085): the scheduler continues the work without cooling the agent. Additive, 20 Oct 2026.
   */
  interrupted?: boolean;
  /** cause 'runtime': the evidence (the signature, how often, an excerpt). Additive, ADR 0089. */
  runtime?: RuntimeFault;
  /** When it failed: the session's last activity. */
  at: Ts;
}

/** What the classifier reads. Agents fill the transcript fields; the hub and the web add what they know. */
export interface FailureSignals {
  status: SessionStatus;
  /** The session's last assistant text (Session.lastText). */
  lastText: string;
  /** The model the session ran with, when known. */
  model?: string | null;
  /** stop_reason of the last assistant message. */
  lastStop?: string | null;
  /** The process exit code, when Orgus ran it. */
  exitCode?: number | null;
  /** An API error a hook reported (StopFailure), when known. */
  apiError?: string | null;
  /** R-HL-3: why a decided request stopped the session ("Blocked: approval expired"). */
  blocked?: string | null;
  /** What Orgus could not do to start it (preflight, folder, settings). */
  launchError?: string | null;
  /** The session's own account of why the work failed (tests red, goal not met). */
  taskFailed?: string | null;
  at: Ts;
}

export const FAILURE_DETAIL_MAX = 500;
const SUMMARY_TEXT_MAX = 160;

/** True when a message is an error Claude Code printed instead of an answer (SESSION_ERROR_RE, missions.ts). */
export function isSessionErrorText(text: string): boolean {
  return !!text && (SESSION_ERROR_RE.test(text) || /^\s*You have (?:hit|reached) your (?:(?:usage|session|weekly) )?limit\b/i.test(text));
}

const MODEL_RE = /issue with the selected model|model (?:\S+ )?(?:was )?not found|model \S+ is not available\b|model_not_found|not_found_error[^}]*\bmodel\b|(?:invalid|unknown) model/i;
const AUTH_RE = /invalid api key|run \/login|not logged in|authentication_error|oauth token (?:has )?(?:expired|been revoked)|\b401\b|permission_error|\b403\b|organization (?:has been )?disabled/i;
const LIMIT_RE = /usage limit|limit reached|(?:hit|reached) your (?:(?:usage|session|weekly) )?limit|rate_limit_error|rate limit|\b429\b|credit balance is too low/i;
const MODEL_NAME_RES = [/selected model \(([^)\s]+)\)/i, /model:\s*([A-Za-z0-9][\w.:@/[\]-]*)/i, /model ([A-Za-z0-9][\w.:@/[\]-]*) (?:was )?not found/i, /model ([A-Za-z0-9][\w.:@/[\]-]*) is not available/i];

function oneLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** No em dash in anything a person reads (AGENTS.md). */
function plain(text: string): string {
  return text.replace(/\s*—\s*/g, ', ');
}

function sentence(text: string): string {
  const t = trimEndOf(plain(clip(text, SUMMARY_TEXT_MAX)), (ch) => '.:;,'.includes(ch) || isRegexSpace(ch));
  return t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}${t.endsWith('…') ? '' : '.'}` : '';
}

function modelNamed(text: string): string | null {
  for (const re of MODEL_NAME_RES) {
    const m = re.exec(text);
    if (m?.[1]) return trimEndOf(m[1], '.,;"\'');
  }
  return null;
}

/** "resets 6pm (Europe/London)" from a limit message, or the epoch seconds of the older "|1760000000" form. */
function resetOf(text: string): { at: Ts | null; words: string | null } {
  const epoch = /\|(\d{10})\b/.exec(text);
  if (epoch) return { at: Number(epoch[1]) * 1000, words: null };
  const m = /\bresets?\s+(?:at\s+)?([^·∙|\n]+?)\s*(?:[·∙|\n]|\.\s|\.?$)/i.exec(text);
  return { at: null, words: m?.[1] ? oneLine(m[1]) : null };
}

function apiReason(text: string): string | null {
  if (/overloaded/i.test(text)) return 'overloaded';
  if (/timed out|timeout|ETIMEDOUT/i.test(text)) return 'timed out';
  if (/connection error|ECONNRESET|ECONNREFUSED|fetch failed|network/i.test(text)) return 'connection error';
  const code = /API Error[:\s(]*(\d{3})\b/i.exec(text);
  if (code) return code[1]!.startsWith('5') ? `server error ${code[1]}` : `request refused, ${code[1]}`;
  return null;
}

/** Classify an error message Claude Code printed (or a hook reported) into a cause. */
function fromErrorText(text: string, model: string | null, at: Ts): SessionFailure {
  const detail = clip(text, FAILURE_DETAIL_MAX);
  if (MODEL_RE.test(text)) {
    const named = modelNamed(text) ?? model;
    return {
      cause: 'model-unavailable',
      summary: `The model ${named ? `${named} ` : ''}does not exist or this account cannot use it.`,
      detail,
      ...(named ? { model: named } : {}),
      at,
    };
  }
  if (/credit balance is too low/i.test(text)) return { cause: 'usage-limit', summary: "The account's credit balance is too low.", detail, at };
  if (LIMIT_RE.test(text) && !/usage limit|limit reached|your (?:(?:usage|session|weekly) )?limit/i.test(text)) {
    return { cause: 'usage-limit', summary: "Anthropic's API rate limit was reached.", detail, at };
  }
  if (LIMIT_RE.test(text)) {
    const r = resetOf(text);
    return {
      cause: 'usage-limit',
      summary: r.words ? `The usage limit was reached; it resets ${plain(r.words)}.` : 'The usage limit was reached.',
      detail,
      ...(r.at !== null ? { resetsAt: r.at } : {}),
      at,
    };
  }
  if (AUTH_RE.test(text)) {
    const key = /invalid api key|x-api-key/i.test(text) && !/run \/login|oauth/i.test(text);
    return { cause: 'auth', summary: key ? 'The API key Claude Code uses is not valid.' : 'Claude Code is signed out on this machine.', detail, at };
  }
  if (/prompt is too long/i.test(text)) return { cause: 'api-error', summary: "The conversation grew past the model's context window.", detail, at };
  const reason = apiReason(text);
  if (reason || /^\s*API Error\b|api_error/i.test(text)) {
    return { cause: 'api-error', summary: `Anthropic's API failed${reason ? ` (${reason})` : ''}.`, detail, at };
  }
  const exited = /Claude Code exited with code (-?\d+)/i.exec(text);
  if (exited) return { cause: 'crash', summary: `Claude Code exited with code ${exited[1]}.`, detail, exitCode: Number(exited[1]), at };
  return { cause: 'crash', summary: 'Claude Code stopped on an error.', detail, at };
}

/**
 * Why a session failed, or null when it did not. Precedence: Orgus could not start it, then a
 * decided request blocked it, then an API error (from a hook or the last message), then a
 * non-zero exit, then the work itself. An ended session fails on its last message or exit; an
 * idle one only on a vendor limit banner (ORGU-96: a limit can end a turn while stream-json stdin
 * keeps the process alive). Blocked and launch hold whatever the status.
 */
export function classifyFailure(sig: FailureSignals): SessionFailure | null {
  const at = sig.at;
  if (sig.launchError?.trim()) {
    return { cause: 'launch', summary: sentence(sig.launchError), detail: clip(sig.launchError, FAILURE_DETAIL_MAX), at };
  }
  if (sig.blocked?.trim()) return { cause: 'blocked', summary: sentence(sig.blocked), at };
  if (sig.status !== 'ended') {
    // A vendor limit can finish a turn while stream-json stdin keeps the process alive.
    if (sig.status === 'idle' && isSessionErrorText(sig.lastText) && LIMIT_RE.test(sig.lastText)) return fromErrorText(sig.lastText, sig.model?.trim() || null, at);
    return null;
  }
  const model = sig.model?.trim() || null;
  if (sig.apiError?.trim()) return fromErrorText(sig.apiError, model, at);
  if (isSessionErrorText(sig.lastText)) return fromErrorText(sig.lastText, model, at);
  if (typeof sig.exitCode === 'number' && sig.exitCode !== 0) {
    const last = sig.lastText.trim();
    return { cause: 'crash', summary: `Claude Code exited with code ${sig.exitCode}.`, ...(last ? { detail: clip(last, FAILURE_DETAIL_MAX) } : {}), exitCode: sig.exitCode, at };
  }
  if (sig.taskFailed?.trim()) return { cause: 'task', summary: sentence(sig.taskFailed), detail: clip(sig.taskFailed, FAILURE_DETAIL_MAX), at };
  return null;
}

/** A session whose commands showed a runtime fault failed for that (cause 'runtime'), with its evidence. */
export function runtimeFailure(f: RuntimeFault, at: Ts = f.at): SessionFailure {
  return { cause: 'runtime', summary: runtimeFaultSentence(f), detail: f.excerpt.slice(0, FAILURE_DETAIL_MAX), runtime: f, at };
}

/**
 * An ended session's failure once its runtime fault is known (ADR 0089): the fault is why it
 * failed unless something more specific of the agent's own said so (signed out, a limit, the
 * model, the API, a blocked request, a launch). A run that ended "fine" with a broken shell did
 * not do its work either.
 */
export function withRuntimeFailure(failure: SessionFailure | null, fault: RuntimeFault | null | undefined, status: SessionStatus): SessionFailure | null {
  if (!fault || status !== 'ended') return failure;
  if (failure && failure.cause !== 'crash' && failure.cause !== 'task' && failure.cause !== 'environment') return failure;
  return runtimeFailure(fault, failure?.at ?? fault.at);
}

function isCause(v: unknown): v is FailureCause {
  return typeof v === 'string' && (FAILURE_CAUSES as readonly string[]).includes(v);
}

/** The failure an agent sent (Session.ext.failure), or null when absent or malformed. */
export function sessionFailureExt(s: Pick<Session, 'ext'> | null | undefined): SessionFailure | null {
  const v = s?.ext?.[SESSION_FAILURE_EXT] as Partial<SessionFailure> | undefined;
  if (!v || typeof v !== 'object' || !isCause(v.cause) || typeof v.summary !== 'string' || typeof v.at !== 'number') return null;
  return v as SessionFailure;
}

/**
 * A session's failure: what its agent sent, else classified here from what the session carries
 * (an older agent sends no ext.failure). `extra` adds what the caller knows (blocked, launch).
 */
export function sessionFailureOf(
  s: Pick<Session, 'status' | 'lastText' | 'model' | 'lastActivity'> & Partial<Pick<Session, 'ext'>>,
  extra: Partial<Pick<FailureSignals, 'blocked' | 'launchError' | 'taskFailed' | 'exitCode'>> = {},
): SessionFailure | null {
  if (extra.blocked || extra.launchError) return classifyFailure({ status: s.status, lastText: s.lastText, model: s.model, at: s.lastActivity, ...extra });
  return sessionFailureExt(s) ?? classifyFailure({ status: s.status, lastText: s.lastText, model: s.model, at: s.lastActivity, ...extra });
}

// ---------------------------------------------------------------------------
// Models (ADR 0059 decision 4: preflight checks the model)
// ---------------------------------------------------------------------------

/** Claude Code's model aliases: each resolves to a model the account can use. */
export const CLAUDE_MODEL_ALIASES: readonly string[] = ['default', 'best', 'opus', 'sonnet', 'haiku', 'opusplan', 'fable', 'mythos'];

/**
 * The model to start `agentKind` with (ADR 0049 decision 5): a model goes to the kind it was
 * chosen for, or to a kind that advertises it (ACP `configOptions`, category `model`); any other
 * kind runs its own default. No model names are matched here.
 */
export function modelForAgentKind(model: string | null | undefined, agentKind: string, chosenFor: string, advertised: readonly string[] = []): string | null {
  const raw = model?.trim();
  if (!raw) return null;
  return agentKind === chosenFor || advertised.includes(raw) ? raw : null;
}

export type ModelCheck =
  | { verdict: 'ok' }
  /** Not a model that exists: refuse the launch. */
  | { verdict: 'unknown'; message: string; suggest: string[] }
  /** Newer than any model Orgus knows, or not a Claude id: launch, and say so. */
  | { verdict: 'unsure'; message: string; suggest: string[] };

interface ParsedModel {
  family: string;
  version: number[];
}

/** 'claude-haiku-5-5' gives haiku [5, 5]; a dated or provider-prefixed id gives the same. */
function parseClaude(id: string): ParsedModel | null {
  const m = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?(?:@\d{8})?(?:-v\d+(?::\d+)?)?$/.exec(id);
  if (!m) return null;
  return { family: m[1]!, version: m[3] !== undefined ? [Number(m[2]), Number(m[3])] : [Number(m[2])] };
}

function compareVersion(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d) return d;
  }
  return 0;
}

/** The newest model Orgus knows in a family ('haiku' gives 'claude-haiku-5-5'), or null. */
export function newestModelOf(family: string): string | null {
  let best: { id: string; v: number[] } | null = null;
  for (const id of KNOWN_CLAUDE_MODELS) {
    const p = parseClaude(id);
    if (!p || p.family !== family) continue;
    if (!best || compareVersion(p.version, best.v) > 0) best = { id, v: p.version };
  }
  return best?.id ?? null;
}

function knownExactly(id: string): boolean {
  if (KNOWN_CLAUDE_MODELS.some((k) => id === k || (id.startsWith(k) && /^(?:-\d{8}|@\d{8})(?:-v\d+(?::\d+)?)?$/.test(id.slice(k.length))))) return true;
  // claude-opus-4-0 is claude-opus-4: the same family and version.
  const p = parseClaude(id);
  return !!p && KNOWN_CLAUDE_MODELS.some((k) => {
    const q = parseClaude(k);
    return !!q && q.family === p.family && compareVersion(q.version, p.version) === 0;
  });
}

/**
 * Whether Claude Code can start with this model id. Aliases and ids Orgus knows are fine; an id
 * of a known family no newer than its newest known model is unknown (claude-haiku-5, when Haiku
 * 5.5 is the newest Haiku); a newer or non-Claude id is unsure: it may be real, so it warns.
 */
export function checkModelId(model: string | null | undefined): ModelCheck {
  const raw = model?.trim();
  if (!raw) return { verdict: 'ok' };
  const bare = raw.replace(/\[1m\]$/i, '').toLowerCase();
  if (CLAUDE_MODEL_ALIASES.includes(bare)) return { verdict: 'ok' };
  const id = normalizeModelId(bare);
  if (knownExactly(id)) return { verdict: 'ok' };
  const p = parseClaude(id);
  const newest = p ? newestModelOf(p.family) : null;
  const suggest = [...(newest ? [newest] : []), 'opus', 'sonnet', 'haiku'];
  if (p && newest && compareVersion(p.version, parseClaude(newest)!.version) <= 0) {
    return { verdict: 'unknown', message: `The model ${raw} does not exist. Use ${newest}, or an alias such as opus, sonnet or haiku.`, suggest };
  }
  if (p && newest) return { verdict: 'unsure', message: `Orgus does not know the model ${raw} yet; Claude Code will try it.`, suggest };
  return { verdict: 'unsure', message: `Orgus does not know the model ${raw}; Claude Code will try it.`, suggest: ['opus', 'sonnet', 'haiku'] };
}

/**
 * The model to retry a model-unavailable failure with: the newest of the same family among the
 * models Orgus knows and the models sessions on the machine answered with (`proven`), else the
 * family's alias, else 'sonnet'.
 */
export function retryModelFor(failed: string | null | undefined, proven: readonly string[] = []): string {
  const id = failed ? normalizeModelId(failed.replace(/\[1m\]$/i, '')) : '';
  const family = parseClaude(id)?.family ?? /claude-([a-z]+)/.exec(id)?.[1] ?? null;
  if (!family) return 'sonnet';
  let best: { id: string; v: number[] } | null = null;
  const newest = newestModelOf(family);
  for (const m of [...(newest ? [newest] : []), ...proven]) {
    const q = parseClaude(normalizeModelId(m));
    if (!q || q.family !== family || normalizeModelId(m) === id || checkModelId(m).verdict === 'unknown') continue;
    if (!best || compareVersion(q.version, best.v) > 0) best = { id: m, v: q.version };
  }
  if (best) return best.id;
  return CLAUDE_MODEL_ALIASES.includes(family) ? family : 'sonnet';
}
